/**
 * SIT Wearable — USB `export` decoder (firmware v0.5.15+).
 *
 * Transport-agnostic: it takes the framed text lines produced by the device's
 * `export` command and turns them into decoded, CRC-validated records grouped
 * by session. The identical record bytes are delivered over BLE, so nothing in
 * here assumes serial — feed it lines from any source.
 *
 * Pipeline:
 *   raw lines  ──parseExportStream──▶  { banner, records[], aborted[], errors[] }
 *   record.bytes ──decodeRecord──▶  header fields + low-g/high-g waveforms
 *
 * Authoritative spec: firmware repo docs/engineering-guide/receiver-protocol.md
 * §7.2/7.3 (record format), §7.4 (USB framing).
 */

import { t } from "i18n";

// ── CRC-16/CCITT ────────────────────────────────────────────────────
// poly 0x1021, init 0xFFFF, no input/output reflection, no final XOR.
export function crc16ccitt(bytes, start = 0, end = bytes.length) {
  let crc = 0xffff;
  for (let i = start; i < end; i++) {
    crc ^= bytes[i] << 8;
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc & 0xffff;
}

// ── Constants & label maps ──────────────────────────────────────────

export const REC_MAGIC = 0x53495731; // "SIW1"
export const REC_VERSION = 4;
// Accepted header versions. Two firmware lineages both stamp rec_version 5:
//  • TAIL lineage (fix/chunked-store-erase → merged v7, fw v0.5.74+): the
//    CRC span and offsets stay identical to v4 (body_crc16 @67, hdr_crc16
//    @69, CRC over [0,69)) and new fields append AFTER hdr_crc16, deliberately
//    uncovered: rec_type @71 (v5), slot_sectors @72 (v6), trigger_band @73
//    (v7). v7 (fw v0.5.92, merge of wearable-firmware#17) renumbered the
//    session markers to rec_type 2/3 so SNIPPET keeps the 1 it has carried
//    since v5 — verified against sit_impact_store.c's store_rec_hdr_t.
//    v8 (fw v0.6.2, wearable-firmware#19) continues the same tail: gate_flags
//    @74, CRC span and every existing offset unchanged.
//    v9 (fw v0.6.4, SPO-1136) adds peak rotation: gyro_valid @75,
//    peak_gyro_mdps @76 (u32), gyro x/y/z @80/@84/@88 (i32).
//    v10 (fw v0.6.8, SPO-1140) adds the 102-byte per-event feature vector at
//    @92 — context fields @92..103 then band[9][10] @104..193. Header is 194 B
//    against the fixed 256 B body offset, so bodies are unmoved.
//    Every one of these is still AFTER hdr_crc16: the CRC span has been 69
//    bytes since v4 and stays there, which is why an older record still
//    decodes byte-for-byte after a firmware update.
//  • MAINLINE v5 (fw v0.5.69, pre-merge wearable-firmware#17): inserts
//    rec_type @67 and trigger_band @68 INSIDE the covered span — body_crc16
//    moves to @69, hdr_crc16 to @71, CRC over [0,71); markers are 1/2 there.
//    This lineage stopped at v5 and never gained a gate byte.
// decodeRecord disambiguates by which header-CRC placement validates.
// Reject unknown higher versions — the version bumps on layout changes, so
// parsing a future v9 with today's assumptions would be a trap.
export const REC_VERSION_MIN = 4;
// v11 (fw v0.6.16, wearable-firmware#23) adds rec_type 4 = LOAD_BINS: per-30s
// low-g load histograms. It is a pure tail-lineage bump — every header offset
// and the [0,69) CRC span are byte-identical to v10; the version only marks the
// new rec_type/layout (layout 2, size_class 4). Older records still decode
// unchanged.
export const REC_VERSION_MAX = 11;
// Tail-lineage rec_type @71 (v7 numbering; v5/v6 only ever wrote 0/1).
export const REC_TYPE = {
  0: "waveform",
  1: "load snippet",
  2: "session start",
  3: "session end",
  4: "load",
};
// LOAD_BINS scheme 1 lower edges (g); the last bin is open-ended (>= 10 g).
export const LOAD_BIN_EDGES_G = [3, 4, 5, 6, 8, 10];
// Mainline-lineage rec_type @67 (pre-merge numbering).
export const MAINLINE_REC_TYPE = {
  0: "waveform",
  1: "session start",
  2: "session end",
};
// trigger_band (@73 on v7, @68 on mainline v5; also band= on =REC): why the
// record exists — which detection band fired. Never what's inside the body.
export const TRIGGER_BAND = {
  0: "high-g impact",
  1: "low-g event",
  2: "none",
  255: "unknown",
};
// gate_flags (@74, tail lineage v8+): the firmware's DRY-RUN pulse-width
// verdict on the closed event — was the pulse too brief, or too sustained, to
// be a ball-to-head impact? It is RECORDED AND NEVER ENFORCED: the record
// exists precisely because nothing was rejected. A "too narrow"/"too wide"
// record is still a real captured event, not a decode problem — do not treat
// it as one. A bitfield, so an event can fail both tests at once.
export const GATE_FLAG = {
  PASS: 0x00,
  TOO_NARROW: 0x01, // over_count below the floor  (default 3 samples)
  TOO_WIDE: 0x02, // duration_ms above the ceiling (default 200 ms)
  NO_ROTATION: 0x04, // peak |w| below the floor (default 170 dps, v9+)
  NOT_EVAL: 0x80, // not a high-band event (snippet, marker, raw chunk)
  UNKNOWN: 0xff, // v7 or older: never carried one
};

/**
 * Human label for a gate_flags byte. Mirrors the firmware's
 * sit_impact_detect_gate_str() so the console and this app read alike.
 * UNKNOWN is checked first because 0xFF also has NOT_EVAL set.
 */
export function gateFlagsLabel(flags) {
  if (flags === GATE_FLAG.UNKNOWN) return "unknown";
  if (flags & GATE_FLAG.NOT_EVAL) return "not evaluated";
  const failed = [];
  if (flags & GATE_FLAG.TOO_NARROW) failed.push("too narrow");
  if (flags & GATE_FLAG.TOO_WIDE) failed.push("too wide");
  if (flags & GATE_FLAG.NO_ROTATION) failed.push("no rotation");
  return failed.length ? failed.join(" + ") : "pass";
}

// ── Per-event feature vector (rec_version 10+, SPO-1140) ────────────
// Measured for every event and GATED ON NOTHING. Amplitude alone cannot tell a
// header from an elbow — both read ~15 g — so the firmware also records what
// was happening around the impact and what the pulse looked like. These fields
// are what turn the dry-run gate thresholds into evidence-based numbers once a
// session has been labelled against video.
export const FEAT_BANDS = 10; // 15 Hz each, 0–150 Hz
export const FEAT_BAND_HZ = 15;
export const FEAT_AXES = [
  "lowG.x",
  "lowG.y",
  "lowG.z",
  "highG.x",
  "highG.y",
  "highG.z",
  "gyro.x",
  "gyro.y",
  "gyro.z",
];
export const FEAT_FLAG = {
  PRE_FULL: 0x01, // the whole 1.5 s pre-window was still in RAM
  POST_FULL: 0x02, // the whole 1.5 s post-window was still in RAM
  GYRO: 0x04, // gyro stream present, so the gyro axes are meaningful
  SPECTRA: 0x08, // band[] was populated
};

// Band power is stored as one byte on a log scale:
//   stored = clamp(round(2 * 10*log10(P)) + 40, 0, 255)
// P is in mg² (both accelerometer channels) or dps² (gyro). 0 doubles as "not
// computed" — read the flags, not the bytes, to tell that from true silence.
export function featBandDb(stored) {
  return (stored - 40) / 2;
}

function decodeFeatures(view, base) {
  const u8 = (o) => view.getUint8(base + o);
  const u16 = (o) => view.getUint16(base + o, true);
  const flags = u8(5);
  const bands = [];
  for (let a = 0; a < FEAT_AXES.length; a++) {
    const row = [];
    for (let b = 0; b < FEAT_BANDS; b++) row.push(u8(12 + a * FEAT_BANDS + b));
    bands.push(row);
  }
  return {
    // Context, from the low-g channel. The pre-window is the discriminative
    // half: a jumped header shows 200–400 ms near 0 g while airborne, and
    // running shows a ~3 Hz gait rhythm that equipment on a bench does not.
    preMinMg: u16(0), // freefall flag — near 0 means airborne
    preMinMsBefore: u16(2),
    gaitCrossings: u8(4),
    postPeakMg: u16(6), // the landing, if one was found
    postPeakMsAfter: u16(8),
    decayMs: u16(10), // mount ring-down: peak → first fall below peak/e
    flags,
    preWindowComplete: (flags & FEAT_FLAG.PRE_FULL) !== 0,
    postWindowComplete: (flags & FEAT_FLAG.POST_FULL) !== 0,
    gyroPresent: (flags & FEAT_FLAG.GYRO) !== 0,
    spectraPresent: (flags & FEAT_FLAG.SPECTRA) !== 0,
    // band[axis][bin] as stored bytes; use featBandDb() for dB.
    bands,
    // Which 15 Hz band held the most energy, per axis. A compliant ball loads
    // low and slow; a rigid contact (skull, elbow, ground) rings higher.
    dominantBand: bands.map((row) => {
      let best = -1;
      let bestV = 0;
      row.forEach((v, i) => {
        if (v > bestV) {
          bestV = v;
          best = i;
        }
      });
      return best;
    }),
  };
}

export const HEADER_PAGE_BYTES = 256;
// hdr_crc16 coverage: bytes 0..68 INCLUSIVE — i.e. the header fields AND the
// body_crc16 field, everything before hdr_crc16 itself. The spec table says
// "bytes 0–66", but that's wrong: verified against real device records
// (fw v0.5.26, rec_version 4), crc(0..68) matches the stored value exactly
// while crc(0..66) does not.
export const HEADER_CRC_LEN = 69;
const SOURCE_HZ = 960;
// Each body block stores 3 axes per sample ({x,y,z}). The header's `channels`
// field (6) counts axes across BOTH bands, not per block — so the per-sample
// stride within a block is 3 × bytes_per_sample, never channels × …
const AXES_PER_BLOCK = 3;

// Below this resultant peak (g) a record is a low-g "event" (manoeuvre/turn
// loading); at or above it, a high-g "impact" (safety). There is no explicit
// band byte in the record — classification is by peak magnitude vs. the
// configured impact threshold. 7 g is the firmware default.
export const DEFAULT_IMPACT_THRESHOLD_G = 7;

export const SIZE_CLASS = {
  0: { label: "XS", seconds: 0.5 },
  1: { label: "S", seconds: 1 },
  2: { label: "M", seconds: 2 },
  3: { label: "L", seconds: 3 },
};

export const IMU_PROFILE = { 0: "FULL", 1: "IMPACT", 2: "OFF" };
export const LOWG_MODE = { 1: "AVG" };
export const HIGHG_MODE = { 2: "MAX-MAG" };

// ── Record binary decode ────────────────────────────────────────────

/**
 * Decode one raw record (256-byte header page + body) into structured data.
 *
 * @param {Uint8Array} bytes  full record bytes (header page + body)
 * @param {object} [opts]
 * @param {number} [opts.impactThresholdG]  peak >= this ⇒ "impact", else "event"
 * @returns {object} decoded record with `.valid` flags; never throws.
 */
export function decodeRecord(bytes, opts = {}) {
  const impactThresholdG = opts.impactThresholdG ?? DEFAULT_IMPACT_THRESHOLD_G;

  const valid = {
    magic: false,
    version: false,
    hdrCrc: false,
    bodyCrc: false,
    length: false,
  };
  const errors = [];

  if (!bytes || bytes.length < HEADER_PAGE_BYTES) {
    return {
      valid,
      errors: [`record too short (${bytes ? bytes.length : 0} bytes)`],
    };
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const u8 = (o) => view.getUint8(o);
  const u16 = (o) => view.getUint16(o, true);
  const u32 = (o) => view.getUint32(o, true);
  const i32 = (o) => view.getInt32(o, true);
  const u64 = (o) => Number(view.getBigUint64(o, true));

  const magic = u32(0);
  const recVersion = u16(4);
  valid.magic = magic === REC_MAGIC;
  valid.version =
    recVersion >= REC_VERSION_MIN && recVersion <= REC_VERSION_MAX;
  if (!valid.magic) errors.push(`bad magic 0x${magic.toString(16)}`);
  if (!valid.version) errors.push(`unexpected rec_version ${recVersion}`);

  const sizeClass = u16(6);
  const impactId = u16(8);
  const sampleCount = u16(10);
  const odrHzDiv10 = u8(12);
  const channels = u8(13);
  const bytesPerSample = u8(14);
  const layout = u8(15);
  const timestampMs = u64(16);
  const peakMagMg = u32(24);
  const peakXMg = i32(28);
  const peakYMg = i32(32);
  const peakZMg = i32(36);
  const riseMs = u16(40);
  const durationMs = u16(42);
  const overCount = u16(44);
  const lowgFsG = u16(46);
  const highgFsG = u16(48);
  const imuProfile = u8(50);
  const filterFlags = u8(51);
  const lowgDecim = u8(52);
  const highgDecim = u8(53);
  const lowgMode = u8(54);
  const highgMode = u8(55);
  const sessionId = u16(56);
  const sessionStartMs = u64(58);
  const timeFlags = u8(66);

  // rec_version 5 is ambiguous (two lineages, see the constants block); the
  // header CRC disambiguates — exactly one placement validates. Prefer the
  // branch layout when its CRC passes (it is the one verified against this
  // fleet's hardware); when neither passes (corrupt header), assume the
  // layout the version implies so the failure report reads sensibly.
  const branchCrcOk = crc16ccitt(bytes, 0, HEADER_CRC_LEN) === u16(69);
  const mainlineCrcOk = recVersion >= 5 && crc16ccitt(bytes, 0, 71) === u16(71);
  const mainline5 =
    recVersion >= 5 && !branchCrcOk && (mainlineCrcOk || recVersion === 5);

  const bodyCrc16 = mainline5 ? u16(69) : u16(67);
  valid.hdrCrc = mainline5 ? mainlineCrcOk : branchCrcOk;
  if (!valid.hdrCrc) errors.push("header CRC mismatch");

  // Record kind + trigger band, by layout. Tail-lineage rec_type sits @71
  // AFTER the CRC span (a v4 record has 0xFF blank flash there — only trust
  // it at v5+) with trigger_band @73 from v7; mainline rec_type/trigger_band
  // sit @67/@68 inside the span. Marker numbering differs: mainline 1/2,
  // tail v7 2/3 (snippet kept 1).
  const recType = mainline5 ? u8(67) : recVersion >= 5 ? u8(71) : 0;
  const triggerBand = mainline5 ? u8(68) : recVersion >= 7 ? u8(73) : 255;
  // Tail lineage only — the mainline v5 branch never gained a gate byte.
  const gateFlags = !mainline5 && recVersion >= 8 ? u8(74) : GATE_FLAG.UNKNOWN;
  // Peak rotation over the event (v9+). gyroValid distinguishes "no rotation
  // measured" from "a genuine zero" — the firmware skips the rotation gate
  // rather than failing it when no gyro sample reached the detector.
  const hasGyro = !mainline5 && recVersion >= 9;
  const gyroValid = hasGyro && u8(75) !== 0;
  const peakGyroMdps = gyroValid ? u32(76) : 0;
  const gyroXMdps = gyroValid ? i32(80) : 0;
  const gyroYMdps = gyroValid ? i32(84) : 0;
  const gyroZMdps = gyroValid ? i32(88) : 0;
  // Per-event feature vector (v10+). Measured and stored, gated on nothing —
  // see FEATURES/sit_impact_features.h in the firmware for what each is for.
  const hasFeatures = !mainline5 && recVersion >= 10;
  const features = hasFeatures ? decodeFeatures(view, 92) : null;
  const isMarker = mainline5
    ? recType === 1 || recType === 2
    : recVersion >= 5 && (recType === 2 || recType === 3);
  const startType = mainline5 ? 1 : 2;
  const markerKind = !isMarker
    ? null
    : recType === startType
    ? "session_start"
    : "session_end";
  // Waveform snippets: mainline says so via the low-g trigger band; the tail
  // lineage via rec_type 1 (load snippet).
  const isSnippet = mainline5 ? !isMarker && triggerBand === 1 : recType === 1;

  // Time reference: bit0 → timestamp is epoch ms; bit1 → session_start epoch.
  // Bits clear ⇒ device-uptime ms (orderable within a session, not absolute).
  const timestampIsEpoch = (timeFlags & 0b1) !== 0;
  const sessionStartIsEpoch = (timeFlags & 0b10) !== 0;

  // ── LOAD_BINS records (rec_type 4, layout 2, fw v0.6.16 / rec_version 11) ──
  // A packed array of 24-byte low-g load-histogram rows, one per 30 s window.
  // NOT a waveform: it must not fall into the interleaved / split-decim body
  // decoders below (layout 2), so decode the rows and return here. Row layout
  // (LE) matches the firmware struct <I 6H I H B B>:
  //   0  start_offset_ms u32 (window start − session_start_ms)
  //   4  counts[6]       6×u16 (episodes per bin, edges LOAD_BIN_EDGES_G)
  //   16 sum_mg          u32   (Σ episode peaks, milli-g)
  //   20 peak_mg         u16   (largest episode peak, milli-g)
  //   22 bin_scheme      u8    (1 for now)
  //   23 flags           u8    (bit0 = partial window)
  if (recType === 4) {
    const ROW_BYTES = 24;
    const rows = Math.floor(sampleCount / 2); // sample_count is body / 12 B
    const bodyEnd = HEADER_PAGE_BYTES + rows * ROW_BYTES;
    valid.length = bytes.length >= bodyEnd;
    if (!valid.length) {
      errors.push(
        `load body truncated: need ${bodyEnd} bytes, have ${bytes.length}`
      );
    }
    if (valid.length) {
      valid.bodyCrc =
        crc16ccitt(bytes, HEADER_PAGE_BYTES, bodyEnd) === bodyCrc16;
      if (!valid.bodyCrc) errors.push("body CRC mismatch");
    }
    const loadWindows = [];
    if (valid.length) {
      for (let i = 0; i < rows; i++) {
        const o = HEADER_PAGE_BYTES + i * ROW_BYTES;
        const startOffsetMs = u32(o);
        const counts = [
          u16(o + 4),
          u16(o + 6),
          u16(o + 8),
          u16(o + 10),
          u16(o + 12),
          u16(o + 14),
        ];
        const sumMg = u32(o + 16);
        const peakMg = u16(o + 20);
        const binScheme = u8(o + 22);
        const flags = u8(o + 23);
        loadWindows.push({
          startOffsetMs,
          // Absolute start = session start + offset; epoch ms when the session
          // clock was epoch-synced (bit1), else device-uptime ms.
          windowStartMs: sessionStartMs + startOffsetMs,
          windowStartIsEpoch: sessionStartIsEpoch,
          counts,
          sumG: sumMg / 1000,
          peakG: peakMg / 1000,
          binScheme,
          partial: (flags & 0b1) !== 0,
        });
      }
    }
    return {
      impactId,
      sessionId,
      recVersion,
      recType,
      recTypeLabel: REC_TYPE[recType] || `?${recType}`,
      isLoad: true,
      isMarker: false,
      isSnippet: false,
      band: "load",
      headerLayout: "tail",
      sizeClass,
      sampleCount,
      layout,
      timestampMs,
      timestampIsEpoch,
      sessionStartMs,
      sessionStartIsEpoch,
      binScheme: loadWindows[0]?.binScheme ?? null,
      binEdgesG: LOAD_BIN_EDGES_G,
      windowCount: loadWindows.length,
      loadWindows,
      lowG: [],
      highG: [],
      valid,
      isValid:
        valid.magic &&
        valid.version &&
        valid.hdrCrc &&
        valid.bodyCrc &&
        valid.length,
      errors,
    };
  }

  // raw → g. Low-g: fs_g / 32768 (0.488 mg/LSB at ±16 g — the range spans
  // the full int16). High-g: the ±320 g range does NOT span the int16, so
  // fs-based conversion reads 6.25% low — the sensor's real sensitivity is
  // exactly 125/12 mg/LSB (96 LSB/g), regardless of the header's fs field.
  // Raw counts are raw counts, so this is correct for every firmware vintage.
  // Gravity is present (no high-pass).
  const lowGainG = lowgFsG / 32768;
  const highGainG = 125 / 12 / 1000;

  // Impact windows are peak-centred: timestamp is the peak instant, so the
  // window starts half a window earlier. A load snippet's timestamp is the
  // window START (body covers [start, start + 2.0 s]) and is NOT peak-centred,
  // so there is no meaningful peak offset to mark.
  const halfWindowMs = sampleCount / 2 / (SOURCE_HZ / 1000);
  const windowStartMs = isSnippet ? timestampMs : timestampMs - halfWindowMs;
  const peakTimeMs = isSnippet || isMarker ? null : timestampMs - windowStartMs;

  // ── Body layout (header byte @15) ──
  // INTERLEAVED (0): the centrifuge RAW writer stores `sampleCount` 12-byte
  //   slots {low x,y,z, high x,y,z} (int16 LE), both bands at the source rate
  //   (decim 1). Older records (recVersion < 3) are interleaved too.
  // SPLIT (1): impact records store a contiguous low-g block then a high-g
  //   block, each decimated by its own factor.
  const interleaved = layout === 0 || recVersion < 3;
  const axisStride = AXES_PER_BLOCK * bytesPerSample; // one band's xyz = 6 bytes
  const mkSample = (t, x, y, z) => ({
    t,
    x,
    y,
    z,
    mag: Math.sqrt(x * x + y * y + z * z),
  });

  let lowG = [];
  let highG = [];

  if (interleaved) {
    const slotStride = 2 * axisStride; // low xyz + high xyz = 12 bytes
    const bodyEnd = HEADER_PAGE_BYTES + sampleCount * slotStride;

    valid.length = bytes.length >= bodyEnd;
    if (!valid.length) {
      errors.push(
        `body truncated: need ${bodyEnd} bytes, have ${bytes.length}`
      );
    }
    if (valid.length && bytes.length >= HEADER_PAGE_BYTES) {
      valid.bodyCrc =
        crc16ccitt(bytes, HEADER_PAGE_BYTES, bytes.length) === bodyCrc16;
      if (!valid.bodyCrc) errors.push("body CRC mismatch");
    }

    if (valid.length) {
      const dtMs = (1 / SOURCE_HZ) * 1000; // decim 1 → source rate
      for (let i = 0; i < sampleCount; i++) {
        const o = HEADER_PAGE_BYTES + i * slotStride;
        if (o + slotStride > bytes.length) break;
        const t = i * dtMs;
        lowG.push(
          mkSample(
            t,
            view.getInt16(o, true) * lowGainG,
            view.getInt16(o + 2, true) * lowGainG,
            view.getInt16(o + 4, true) * lowGainG
          )
        );
        highG.push(
          mkSample(
            t,
            view.getInt16(o + axisStride, true) * highGainG,
            view.getInt16(o + axisStride + 2, true) * highGainG,
            view.getInt16(o + axisStride + 4, true) * highGainG
          )
        );
      }
    }
  } else {
    const nLow = lowgDecim ? Math.floor(sampleCount / lowgDecim) : 0;
    const nHigh = highgDecim ? Math.floor(sampleCount / highgDecim) : 0;
    const lowBlockStart = HEADER_PAGE_BYTES;
    const lowBlockEnd = lowBlockStart + nLow * axisStride;
    const highBlockEnd = lowBlockEnd + nHigh * axisStride;

    valid.length = bytes.length >= highBlockEnd;
    if (!valid.length) {
      errors.push(
        `body truncated: need ${highBlockEnd} bytes, have ${bytes.length}`
      );
    }
    if (valid.length && bytes.length >= lowBlockStart) {
      valid.bodyCrc =
        crc16ccitt(bytes, lowBlockStart, bytes.length) === bodyCrc16;
      if (!valid.bodyCrc) errors.push("body CRC mismatch");
    }

    const readBlock = (byteStart, n, decim, gainG) => {
      const samples = [];
      const dtMs = (decim / SOURCE_HZ) * 1000;
      for (let i = 0; i < n; i++) {
        const o = byteStart + i * axisStride;
        if (o + axisStride > bytes.length) break;
        samples.push(
          mkSample(
            i * dtMs,
            view.getInt16(o, true) * gainG,
            view.getInt16(o + 2, true) * gainG,
            view.getInt16(o + 4, true) * gainG
          )
        );
      }
      return samples;
    };

    lowG = valid.length
      ? readBlock(lowBlockStart, nLow, lowgDecim, lowGainG)
      : [];
    highG = valid.length
      ? readBlock(lowBlockEnd, nHigh, highgDecim, highGainG)
      : [];
  }

  const peakMagG = peakMagMg / 1000;
  // Classification: a recorded trigger band is authoritative (band 2/255 —
  // centrifuge raw chunks, bench snapshots — falls back to the heuristic);
  // tail v5/v6 classifies by rec_type; v4 has neither, so it is peak-vs-
  // threshold all the way.
  let band;
  if (isMarker) band = "marker";
  else if (triggerBand === 0) band = "impact";
  else if (triggerBand === 1) band = "event";
  else if (isSnippet) band = "event";
  else if (!mainline5 && recVersion >= 5 && recVersion < 7) band = "impact";
  else band = peakMagG >= impactThresholdG ? "impact" : "event";

  const sizeInfo = SIZE_CLASS[sizeClass] || {
    label: `?${sizeClass}`,
    seconds: null,
  };

  return {
    // identity
    impactId,
    sessionId,
    recVersion,
    // record type (v5+): 0 = impact, 1 = load snippet. For a snippet, impactId
    // is the broadcast event_id (u8, wraps at 256).
    recType,
    recTypeLabel: mainline5
      ? MAINLINE_REC_TYPE[recType] || `?${recType}`
      : REC_TYPE[recType] || `?${recType}`,
    isSnippet,
    // session boundary markers (mainline v5): header-only records framing
    // each recording interval. A start with no end = session cut short.
    isMarker,
    markerKind,
    // why the record exists (mainline v5): which detection band fired.
    triggerBand,
    triggerBandLabel: TRIGGER_BAND[triggerBand] || `?${triggerBand}`,
    // dry-run pulse-width verdict (v8+). Diagnostic only — the firmware kept
    // the event regardless, so this never means the record is suspect.
    gateFlags,
    gateFlagsLabel: gateFlagsLabel(gateFlags),
    // peak rotation during the event (v9+); null-ish when never measured.
    gyroValid,
    peakGyroMdps,
    gyroXMdps,
    gyroYMdps,
    gyroZMdps,
    // per-event feature vector (v10+), or null on older records.
    features,
    headerLayout: mainline5 ? "mainline" : "tail",
    // classification
    band,
    impactThresholdG,
    // size / sampling
    sizeClass,
    sizeLabel: sizeInfo.label,
    windowSeconds: sizeInfo.seconds,
    sampleCount,
    odrHz: odrHzDiv10 * 10,
    channels,
    layout,
    // timing
    timestampMs,
    timestampIsEpoch,
    sessionStartMs,
    sessionStartIsEpoch,
    windowStartMs,
    peakTimeMs,
    riseMs,
    durationMs,
    overCount,
    // peaks (exact, from header, in g)
    peakMagG,
    peakXG: peakXMg / 1000,
    peakYG: peakYMg / 1000,
    peakZG: peakZMg / 1000,
    // sensor config
    lowgFsG,
    highgFsG,
    imuProfile,
    imuProfileLabel: IMU_PROFILE[imuProfile] || String(imuProfile),
    filterFlags,
    lowgDecim,
    highgDecim,
    lowgMode,
    lowgModeLabel: LOWG_MODE[lowgMode] || String(lowgMode),
    highgMode,
    highgModeLabel: HIGHG_MODE[highgMode] || String(highgMode),
    lowgHz: lowgDecim ? SOURCE_HZ / lowgDecim : null,
    highgHz: highgDecim ? SOURCE_HZ / highgDecim : null,
    // waveforms (g units, t in ms from window start)
    lowG,
    highG,
    // validation
    valid,
    isValid:
      valid.magic &&
      valid.version &&
      valid.hdrCrc &&
      valid.bodyCrc &&
      valid.length,
    errors,
  };
}

// ── Framed stream parse ─────────────────────────────────────────────

// The device console also emits unsolicited log lines at any time. The framing
// rule is: every protocol line starts with "=". Discard anything else.
const EXPORT_REFUSED = /\[export\]\s*refused/i;

function parseBannerTokens(line) {
  // v1: "=EXPORT v1 n=227 fw=0.5.15 mac=AA:BB:CC:DD:EE:FF"
  // v2: "=EXPORT v2 enc=base64 n=780 fw=0.5.41 mac=…"
  const out = {
    version: null,
    recordCount: null,
    fw: null,
    mac: null,
    enc: null,
  };
  const parts = line.slice(1).trim().split(/\s+/); // drop leading "="
  // parts[0] = "EXPORT", parts[1] = "v1"/"v2"
  if (parts[1] && /^v\d+$/i.test(parts[1])) {
    out.version = parseInt(parts[1].slice(1), 10);
  }
  for (const tok of parts.slice(2)) {
    const eq = tok.indexOf("=");
    if (eq === -1) continue;
    const key = tok.slice(0, eq);
    const val = tok.slice(eq + 1);
    if (key === "n") out.recordCount = parseInt(val, 10);
    else if (key === "fw") out.fw = val;
    else if (key === "mac") out.mac = val.toUpperCase();
    else if (key === "enc") out.enc = val;
  }
  return out;
}

function parseKvTokens(line) {
  const out = {};
  for (const tok of line.slice(1).trim().split(/\s+/).slice(1)) {
    const eq = tok.indexOf("=");
    if (eq === -1) continue;
    out[tok.slice(0, eq)] = tok.slice(eq + 1);
  }
  return out;
}

function hexToBytes(hex) {
  const clean = hex.replace(/[^0-9a-fA-F]/g, "");
  const len = clean.length >> 1;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = parseInt(clean.substr(i * 2, 2), 16);
  }
  return bytes;
}

// v0.5.41+ (=EXPORT v2 enc=base64) encodes each =D payload as base64. Decode
// each line INDEPENDENTLY (a line carries its own `=` padding), then concatenate
// the resulting bytes — never concatenate the base64 text across lines.
export function base64ToBytes(b64) {
  const clean = String(b64).replace(/[^A-Za-z0-9+/=]/g, "");
  if (!clean) return new Uint8Array(0);
  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function concatBytes(chunks) {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

/**
 * Split CR-merged lines. The console echoes typed input terminated by a bare
 * `\r` (no `\n`), so a `\n`-delimited reader glues the echo onto the next
 * protocol line — e.g. `"export\r=EXPORT v1 n=20 …"` arrives as ONE line and
 * a startsWith("=EXPORT") check misses the banner. Splitting on `\r` restores
 * each fragment as its own line.
 */
export function normalizeConsoleLines(lines) {
  const out = [];
  for (const raw of lines) {
    for (const part of String(raw).split("\r")) {
      out.push(part);
    }
  }
  return out;
}

/**
 * Parse a framed `export` response into records.
 *
 * @param {string[]} lines  every line of the response (may include the
 *   `=EXPORT` banner and stray non-`=` log lines, which are ignored).
 * @param {object} [opts]  forwarded to decodeRecord (e.g. impactThresholdG)
 * @returns {{
 *   version:number|null, fw:string|null, mac:string|null,
 *   recordCount:number|null, records:object[], aborted:object[],
 *   done:boolean, refused:boolean, errors:string[]
 * }}
 */
export function parseExportStream(lines, opts = {}) {
  const result = {
    version: null,
    fw: null,
    mac: null,
    recordCount: null,
    records: [],
    aborted: [],
    done: false,
    refused: false,
    errors: [],
  };

  let cur = null; // { index, len, id, sess, chunks: [Uint8Array] }
  // Payload encoding, from the banner: v2/enc=base64 → base64, else hex (v1).
  let enc = "hex";

  const finalizeRecord = (crcHex) => {
    if (!cur) return;
    const bytes = concatBytes(cur.chunks);
    const framing = {
      byteCountOk: bytes.length === cur.len,
      crcOk: false,
    };
    const declaredCrc = crcHex != null ? parseInt(crcHex, 16) : null;
    framing.crcOk = declaredCrc != null && crc16ccitt(bytes) === declaredCrc;

    const decoded = decodeRecord(bytes, opts);
    decoded.frameIndex = cur.index;
    decoded.rawLen = cur.len;
    decoded.byteCount = bytes.length;
    decoded.declaredType = cur.type;
    decoded.declaredBand = cur.band;
    decoded.framing = framing;
    // A record is only trustworthy if framing AND in-record CRCs all pass.
    decoded.isValid = decoded.isValid && framing.byteCountOk && framing.crcOk;
    if (!framing.byteCountOk) {
      decoded.errors.push(
        `byte count ${bytes.length} != declared len ${cur.len}`
      );
    }
    if (!framing.crcOk) decoded.errors.push("framing CRC mismatch");

    result.records.push(decoded);
    cur = null;
  };

  for (const raw of normalizeConsoleLines(lines)) {
    const line = raw.trim();
    if (!line) continue;

    if (EXPORT_REFUSED.test(line)) {
      result.refused = true;
      result.errors.push(t("utils.wearableExport.exportRefused"));
      continue;
    }

    // Framing rule: ignore anything that isn't a protocol line.
    if (line[0] !== "=") continue;

    if (line.startsWith("=EXPORT")) {
      const banner = parseBannerTokens(line);
      result.version = banner.version;
      result.recordCount = banner.recordCount;
      result.fw = banner.fw;
      result.mac = banner.mac;
      enc =
        banner.enc === "base64" ||
        (banner.version != null && banner.version >= 2)
          ? "base64"
          : "hex";
      result.encoding = enc;
    } else if (line.startsWith("=REC")) {
      // A new =REC while one is open means the previous never got =END/=ABORT.
      if (cur) {
        result.errors.push(`record i=${cur.index} missing =END; dropped`);
        result.aborted.push({ index: cur.index, reason: "no-end" });
        cur = null;
      }
      const kv = parseKvTokens(line);
      cur = {
        index: kv.i != null ? parseInt(kv.i, 10) : null,
        len: kv.len != null ? parseInt(kv.len, 10) : 0,
        id: kv.id != null ? parseInt(kv.id, 10) : null,
        sess: kv.sess != null ? parseInt(kv.sess, 10) : null,
        // optional since mainline v5: record kind + trigger band, declared
        // on the frame so markers are identifiable without decoding.
        type: kv.type != null ? parseInt(kv.type, 10) : null,
        band: kv.band != null ? parseInt(kv.band, 10) : null,
        chunks: [],
      };
    } else if (line.startsWith("=END")) {
      const kv = parseKvTokens(line);
      finalizeRecord(kv.crc);
    } else if (line.startsWith("=ABORT")) {
      const kv = parseKvTokens(line);
      result.aborted.push({
        index: cur ? cur.index : kv.i != null ? parseInt(kv.i, 10) : null,
        reason: "device-abort",
      });
      cur = null; // discard this record, keep earlier ones
    } else if (line.startsWith("=DONE")) {
      // Must precede the "=D" test — "=DONE" also starts with "=D".
      const kv = parseKvTokens(line);
      if (kv.n != null) result.recordCount = parseInt(kv.n, 10);
      result.done = true;
    } else if (line.startsWith("=D")) {
      // Decode each payload line independently, then concatenate the BYTES.
      if (cur) {
        const payload = line.slice(2).trim();
        cur.chunks.push(
          enc === "base64" ? base64ToBytes(payload) : hexToBytes(payload)
        );
      }
    }
  }

  if (cur) {
    result.errors.push(`record i=${cur.index} truncated at end of stream`);
    result.aborted.push({ index: cur.index, reason: "truncated" });
  }

  return result;
}

/**
 * Assemble the records collected by the main-process binary reader (which owns
 * the raw serial stream so v3's inline record bytes — including 0x0A — survive)
 * into the same shape as parseExportStream. Framing (byte count + CRC) is done
 * in main; here we just base64-decode each record block and run decodeRecord.
 *
 * @param {{ banner:object|null, records:object[], aborted:object[],
 *   refused:boolean, done:boolean }} main  the serial:export IPC result
 * @param {object} [opts]  forwarded to decodeRecord
 */
export function buildExportResult(main = {}, opts = {}) {
  const banner = main.banner || {};
  const result = {
    version: banner.version ?? null,
    fw: banner.fw ?? null,
    mac: banner.mac ?? null,
    recordCount: banner.n ?? null,
    encoding: banner.enc ?? null,
    records: [],
    aborted: main.aborted || [],
    done: !!main.done,
    refused: !!main.refused,
    errors: [],
  };

  for (const blk of main.records || []) {
    const bytes = base64ToBytes(blk.bytesB64);
    const framing = {
      byteCountOk: bytes.length === blk.len,
      crcOk: !!blk.crcOk,
    };
    const decoded = decodeRecord(bytes, opts);
    decoded.frameIndex = blk.index;
    decoded.rawLen = blk.len;
    decoded.byteCount = bytes.length;
    decoded.declaredType = blk.type ?? null;
    decoded.declaredBand = blk.band ?? null;
    decoded.framing = framing;
    decoded.isValid = decoded.isValid && framing.byteCountOk && framing.crcOk;
    if (!framing.byteCountOk) {
      decoded.errors.push(
        `byte count ${bytes.length} != declared len ${blk.len}`
      );
    }
    if (!framing.crcOk) decoded.errors.push("framing CRC mismatch");
    result.records.push(decoded);
  }

  return result;
}

/**
 * Find the most recent complete `=EXPORT … =DONE` block in a line array.
 *
 * The device can stream export data unsolicited (e.g. on USB connect), so we
 * watch the raw console and pull out the latest finished block rather than
 * only capturing a block we explicitly asked for.
 *
 * @param {string[]} lines
 * @returns {{ start:number, end:number, lines:string[] }|null} null if no
 *   complete block (a `=DONE` with a preceding `=EXPORT`) is present.
 */
export function sliceLatestExport(lines) {
  // Un-merge CR-glued fragments first (command echo + banner can arrive as
  // one \n-delimited line) so startsWith() sees each protocol line cleanly.
  const norm = normalizeConsoleLines(lines);
  let end = -1;
  for (let i = norm.length - 1; i >= 0; i--) {
    if (norm[i].trim().startsWith("=DONE")) {
      end = i;
      break;
    }
  }
  if (end < 0) return null;

  let start = -1;
  for (let i = end; i >= 0; i--) {
    if (norm[i].trim().startsWith("=EXPORT")) {
      start = i;
      break;
    }
  }
  if (start < 0) return null;

  return { start, end, lines: norm.slice(start, end + 1) };
}

// ── Session grouping & summary ──────────────────────────────────────

/**
 * Group decoded records by session_id, preserving append order within a
 * session and ordering sessions by their start instant.
 */
export function groupBySession(records) {
  const map = new Map();
  for (const rec of records) {
    const key = rec.sessionId;
    if (!map.has(key)) {
      map.set(key, {
        sessionId: key,
        sessionStartMs: rec.sessionStartMs,
        sessionStartIsEpoch: rec.sessionStartIsEpoch,
        records: [],
        markers: [],
        loadWindows: [],
        impactCount: 0,
        eventCount: 0,
        peakMagG: 0,
      });
    }
    const grp = map.get(key);
    if (rec.isMarker) {
      // Session boundaries are metadata, not rows — folded into recording
      // intervals below, never counted as impacts/events.
      grp.markers.push(rec);
      continue;
    }
    if (rec.isLoad) {
      // Load histograms are their own row type (per-30s windows), not impacts:
      // flatten every record's windows into the session so a load view / table
      // reads them directly, and keep them out of the impact/event list.
      for (const w of rec.loadWindows || []) grp.loadWindows.push(w);
      continue;
    }
    grp.records.push(rec);
    if (rec.band === "impact") grp.impactCount += 1;
    else grp.eventCount += 1;
    if (rec.peakMagG > grp.peakMagG) grp.peakMagG = rec.peakMagG;
  }
  const groups = Array.from(map.values()).sort(
    (a, b) => (a.sessionStartMs || 0) - (b.sessionStartMs || 0)
  );
  // Fold start/end markers into recording intervals. Centrifuge sessions emit
  // one START/END pair per tap-to-record run under the same session id. A
  // START with no matching END means the session was cut short (battery died,
  // crash) — surfaced as cutShort, never an invented end time.
  for (const g of groups) {
    g.intervals = [];
    let open = null;
    const marks = [...g.markers].sort((a, b) => a.timestampMs - b.timestampMs);
    for (const m of marks) {
      if (m.markerKind === "session_start") {
        if (open) g.intervals.push(open); // unmatched start → cut short
        open = {
          startMs: m.timestampMs,
          endMs: null,
          isEpoch: m.timestampIsEpoch,
        };
      } else if (open) {
        open.endMs = m.timestampMs;
        g.intervals.push(open);
        open = null;
      } else {
        // END with no START in this export (older records erased mid-session)
        g.intervals.push({
          startMs: null,
          endMs: m.timestampMs,
          isEpoch: m.timestampIsEpoch,
        });
      }
    }
    if (open) g.intervals.push(open);
    g.cutShort = g.intervals.some(
      (iv) => iv.startMs != null && iv.endMs == null
    );
    g.recordedStartMs = g.intervals.length ? g.intervals[0].startMs : null;
    g.recordedEndMs = g.intervals.length
      ? g.intervals[g.intervals.length - 1].endMs
      : null;
    g.recordedIsEpoch = g.intervals.length ? g.intervals[0].isEpoch : false;
    // Load windows in chronological order (records may arrive out of order).
    g.loadWindows.sort(
      (a, b) => (a.windowStartMs || 0) - (b.windowStartMs || 0)
    );
  }
  return groups;
}

/** High-level counts for the summary header. */
export function summarizeExport(parsed) {
  const records = parsed.records || [];
  let impacts = 0;
  let events = 0;
  let markers = 0;
  let loadRecords = 0;
  let invalid = 0;
  let peakMagG = 0;
  const sessions = new Set();
  // Which validation check(s) failed, and how often — an all-records failure
  // of one specific check is a systematic decoder/firmware mismatch, not data
  // corruption, so the breakdown is the diagnosis.
  const failedChecks = {};
  const recVersions = new Set();
  const bump = (k) => {
    failedChecks[k] = (failedChecks[k] || 0) + 1;
  };
  for (const rec of records) {
    if (rec.isMarker) markers += 1;
    else if (rec.isLoad) loadRecords += 1;
    else if (rec.band === "impact") impacts += 1;
    else events += 1;
    if (rec.recVersion != null) recVersions.add(rec.recVersion);
    if (!rec.isValid) {
      invalid += 1;
      if (rec.valid) {
        if (!rec.valid.magic) bump("magic");
        if (!rec.valid.version) bump("record version");
        if (!rec.valid.hdrCrc) bump("header CRC");
        if (!rec.valid.bodyCrc) bump("body CRC");
        if (!rec.valid.length) bump("length");
      }
      if (rec.framing) {
        if (!rec.framing.byteCountOk) bump("byte count");
        if (!rec.framing.crcOk) bump("framing CRC");
      }
    }
    if (rec.peakMagG > peakMagG) peakMagG = rec.peakMagG;
    sessions.add(rec.sessionId);
  }
  return {
    total: records.length,
    expected: parsed.recordCount,
    sessions: sessions.size,
    impacts,
    events,
    markers,
    loadRecords,
    invalid,
    aborted: (parsed.aborted || []).length,
    peakMagG,
    failedChecks,
    recVersions: Array.from(recVersions),
  };
}

import { recordKey } from "./videoReview";

// ── Record-level CSV ────────────────────────────────────────────────
// Extracted from the ops modal so the column contract is testable. A header
// and a row that disagree by one field silently shift the 90 spectral columns
// and quietly poison every plot made from the file, which is not something a
// human spots by eye.

const CSV_BASE_COLUMNS = [
  "session_id",
  "session_start",
  "impact_id",
  "rec_type",
  "band",
  "trigger_band",
  "peak_mag_g",
  "peak_x_g",
  "peak_y_g",
  "peak_z_g",
  "rise_ms",
  "duration_ms",
  "over_count",
  "size",
  "sample_count",
  "timestamp",
  "time_synced",
  "valid",
  // Dry-run verdict + rotation (record v9/v10). Blank on older records.
  "gate",
  "rotation_dps",
];

// Per-event features (v10). These are the columns the header-detection work
// actually needs: the first pass is a scatter of freefall_g against peak_mag_g
// over video-labelled events.
const CSV_FEATURE_COLUMNS = [
  "freefall_g",
  "freefall_ms_before",
  "gait_crossings",
  "landing_g",
  "landing_ms_after",
  "ringdown_ms",
  "window_complete",
];

// Band power in dB, ten 15 Hz bands per axis. Wide, but a band that was not
// exported cannot be recovered without re-reading the device.
const CSV_BAND_COLUMNS = FEAT_AXES.flatMap((ax) =>
  Array.from(
    { length: FEAT_BANDS },
    (_, b) =>
      `psd_${ax.replace(".", "_")}_${b * FEAT_BAND_HZ}_${
        (b + 1) * FEAT_BAND_HZ
      }hz`
  )
);

// Video-review verdict, when a session has been checked against footage. This
// is the ground truth the whole exercise exists to produce: without it the
// feature columns are unlabelled and nothing can be fitted to them.
const CSV_REVIEW_COLUMNS = ["video_verdict", "video_note"];

export const CSV_COLUMNS = [
  ...CSV_BASE_COLUMNS,
  ...CSV_FEATURE_COLUMNS,
  ...CSV_BAND_COLUMNS,
  ...CSV_REVIEW_COLUMNS,
];

function csvFeatureCells(r) {
  if (!r.features) {
    return Array(CSV_FEATURE_COLUMNS.length + CSV_BAND_COLUMNS.length).fill("");
  }
  const f = r.features;
  return [
    (f.preMinMg / 1000).toFixed(3),
    f.preMinMsBefore,
    f.gaitCrossings,
    (f.postPeakMg / 1000).toFixed(3),
    f.postPeakMg ? f.postPeakMsAfter : "",
    f.decayMs,
    f.preWindowComplete && f.postWindowComplete,
    // 0 doubles as "not computed" in the stored byte, so emit blank rather
    // than a number that would average into a real distribution.
    ...f.bands.flatMap((row) =>
      row.map((v) => (v ? featBandDb(v).toFixed(1) : ""))
    ),
  ];
}

// Commas and quotes are entirely plausible in a free-text note, and one
// unescaped comma shifts every column after it.
function csvQuote(v) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

export function buildRecordCSV(records, review) {
  const labels = review?.labels || {};
  // Load-histogram records (rec_type 4) have no impact columns (peak, rise,
  // waveform); they are a separate row type with their own view, so they are
  // not rows in this impact-review CSV.
  const rows = (records || [])
    .filter((r) => !r.isLoad)
    .map((r) =>
      [
        r.sessionId,
        r.sessionStartIsEpoch ? new Date(r.sessionStartMs).toISOString() : "",
        r.impactId,
        r.recTypeLabel ?? "",
        r.band,
        r.triggerBandLabel ?? "",
        r.peakMagG.toFixed(2),
        r.peakXG.toFixed(2),
        r.peakYG.toFixed(2),
        r.peakZG.toFixed(2),
        r.riseMs,
        r.durationMs,
        r.overCount,
        r.sizeLabel,
        r.sampleCount,
        r.timestampIsEpoch
          ? new Date(r.timestampMs).toISOString()
          : r.timestampMs,
        r.timestampIsEpoch,
        r.isValid,
        // Quote the verdict: "too narrow + no rotation" holds no comma today,
        // but the label is a join and a future flag could add one.
        `"${r.gateFlagsLabel ?? ""}"`,
        r.gyroValid ? (r.peakGyroMdps / 1000).toFixed(1) : "",
        ...csvFeatureCells(r),
        csvQuote(labels[recordKey(r)]?.label ?? ""),
        csvQuote(labels[recordKey(r)]?.note ?? ""),
      ].join(",")
    );

  // Headers the device never fired on have no record, so they cannot be rows
  // in the normal sense -- but leaving them out of the file means only
  // precision can be computed, never how many real headers were missed. They
  // go in as their own rec_type with just a time and a note, which is all
  // that is known about them.
  const missedRows = (review?.missed || []).map((m) =>
    CSV_COLUMNS.map((col) => {
      if (col === "rec_type") return "missed_header";
      if (col === "timestamp") return m.sessionMs;
      if (col === "video_verdict") return csvQuote("header");
      if (col === "video_note") return csvQuote(m.note ?? "");
      return "";
    }).join(",")
  );

  return [CSV_COLUMNS.join(","), ...rows, ...missedRows].join("\n");
}
